import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as Icons from 'react-icons/lu';
import { LuArrowRight, LuChevronRight } from 'react-icons/lu';

const CARD_SIZE = 'h-full min-h-[320px]';

/** Returns notch theme for featured cards, or null for default white cards */
function getNotchTheme(service) {
  const id = String(service?.id || '').toLowerCase();
  const title = String(service?.title || '').toLowerCase();

  if (id.includes('mobile') || title.includes('mobile app')) return 'dark';
  // Web Hosting + Data Analysis → company yellow, same notch layout
  if (
    id.includes('hosting') ||
    title.includes('hosting') ||
    id.includes('analysis') ||
    title.includes('analysis')
  ) {
    return 'yellow';
  }
  return null;
}

const NOTCH_THEMES = {
  dark: {
    surface:
      'bg-[#101010] shadow-[0_18px_45px_rgba(0,0,0,0.18)] transition-colors duration-300 group-hover:bg-primary group-hover:shadow-[0_18px_45px_rgba(243,199,21,0.35)]',
    icon: 'text-primary transition-colors duration-300 group-hover:text-black',
    title: 'text-white transition-colors duration-300 group-hover:text-black',
    description: 'text-white/55 transition-colors duration-300 group-hover:text-black/60',
    button:
      'bg-[#101010] text-white shadow-[0_0_0_1px_rgba(255,255,255,0.12)] transition-colors duration-300 group-hover:bg-black group-hover:text-white group-hover:shadow-[0_0_0_1px_rgba(0,0,0,0.15)]',
  },
  yellow: {
    surface:
      'bg-primary shadow-[0_18px_45px_rgba(243,199,21,0.35)] transition-colors duration-300 group-hover:bg-[#101010] group-hover:shadow-[0_18px_45px_rgba(0,0,0,0.18)]',
    icon: 'text-black transition-colors duration-300 group-hover:text-primary',
    title: 'text-black transition-colors duration-300 group-hover:text-white',
    description: 'text-black/60 transition-colors duration-300 group-hover:text-white/55',
    button:
      'bg-black text-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)] transition-colors duration-300 group-hover:bg-[#101010] group-hover:shadow-[0_0_0_1px_rgba(255,255,255,0.12)]',
  },
};

function DefaultServiceCard({ service, index, IconComponent }) {
  return (
    <Link to={`/services/${service.id}`} className="block h-full">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-50px' }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className={`group relative ${CARD_SIZE}`}
      >
        <div className={`relative flex ${CARD_SIZE} cursor-pointer flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white p-7 shadow-[0_2px_20px_rgba(0,0,0,0.04)] transition-all duration-500 hover:border-primary/30 hover:shadow-[0_12px_40px_rgba(243,199,21,0.15)] dark:border-white/10 dark:bg-[#0f0f0f] md:p-8`}>
          <div className="absolute left-0 right-0 top-0 h-1 bg-gradient-to-r from-primary to-yellow-500 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
          <div className="absolute -right-20 -top-20 h-40 w-40 rounded-full bg-gradient-to-br from-primary to-yellow-400 opacity-0 blur-3xl transition-opacity duration-700 group-hover:opacity-[0.08]" />

          <div className="relative mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-all duration-400 group-hover:scale-110 group-hover:rounded-xl group-hover:bg-primary group-hover:text-black group-hover:shadow-lg">
            <IconComponent size={24} />
          </div>

          <h3 className="mb-3 text-xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-black dark:text-white dark:group-hover:text-primary">
            {service.title}
          </h3>

          <p className="mb-6 text-[0.95rem] leading-relaxed text-gray-500 dark:text-gray-300">
            {service.description}
          </p>

          <div className="mt-auto flex items-center gap-2 text-sm font-semibold text-gray-400 transition-all duration-300 group-hover:text-black dark:text-gray-500 dark:group-hover:text-primary">
            <span>Learn More</span>
            <LuArrowRight size={12} className="transform transition-transform duration-300 group-hover:translate-x-1" />
          </div>
        </div>
      </motion.div>
    </Link>
  );
}

/** Notched card — same layout as Mobile App; theme = dark | yellow | white */
function NotchedServiceCard({ service, index, IconComponent, theme = 'dark' }) {
  const colors = NOTCH_THEMES[theme] || NOTCH_THEMES.dark;

  // Wider notch for Montserrat Alternates ("Explore More" is wider than Asap)
  const cardMask = `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="none">
      <path fill="white" d="M7 0H93A7 7 0 0 1 100 7V93A7 7 0 0 1 93 100H46A3.5 3.5 0 0 1 42.5 96.5V87A5 5 0 0 0 37.5 82H0V7A7 7 0 0 1 7 0Z"/>
    </svg>`,
  )}")`;

  return (
    <Link to={`/services/${service.id}`} className="block h-full">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-50px' }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className={`group relative ${CARD_SIZE}`}
      >
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 rounded-[1.75rem] ${colors.surface}`}
          style={{
            WebkitMaskImage: cardMask,
            maskImage: cardMask,
            WebkitMaskSize: '100% 100%',
            maskSize: '100% 100%',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
          }}
        />

        <div className={`relative z-10 flex ${CARD_SIZE} flex-col p-7 pb-3 md:p-8 md:pb-3`}>
          <div className={`mb-7 ${colors.icon}`}>
            <IconComponent size={34} strokeWidth={1.75} />
          </div>

          <h3 className={`mb-3 max-w-[14rem] text-2xl font-extrabold leading-snug tracking-tight ${colors.title}`}>
            {service.title}
          </h3>

          <p className={`mb-6 max-w-sm text-sm leading-relaxed ${colors.description}`}>
            {service.description}
          </p>

          <div className="relative z-20 mt-auto -ml-7 self-start md:-ml-8">
            <div className="rounded-tr-[1.25rem] bg-transparent pt-1.5 pr-2">
              <div
                className={`inline-flex items-center gap-0.5 whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-semibold tracking-tight transition-colors duration-300 ${colors.button}`}
              >
                <span>Explore More</span>
                <LuChevronRight size={14} strokeWidth={2.5} />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </Link>
  );
}

const ServiceCard = ({ service, index }) => {
  const IconComponent = Icons[service.icon] || Icons.LuMonitor;
  const notchTheme = getNotchTheme(service);

  if (notchTheme) {
    return (
      <NotchedServiceCard
        service={service}
        index={index}
        IconComponent={IconComponent}
        theme={notchTheme}
      />
    );
  }

  return <DefaultServiceCard service={service} index={index} IconComponent={IconComponent} />;
};

export default ServiceCard;
